import { useEffect, useLayoutEffect, useState } from 'react';
import Countdown from 'react-countdown';
import { toast } from 'react-toastify';

import classNames from 'classnames';

import { useConfirmVerificationCodeMutation, useSendVerificationCodeQuery } from '@query';

import { getErrorMessage } from '@shared/helpers/error';
import useSelfDataCheckLocalStorage from '@shared/hooks/useSelfDataCheckLocalStorage';

import * as Dialog from '@radix-ui/react-dialog';
import { OtpInput } from '@ui/uikit/components/common/OtpInput';
import { CrossLg, Loading } from '@ui/uikit/components/icons/mono';

interface MagicIdProfileEmailOtpDialogProps extends Dialog.DialogProps {
  validEmail: string;
}

const SEND_TIMEOUT_IN_MS = 60 * 1000;
const OTP_LENGTH = 6;

export default function EmailOtpDialog({
  validEmail,
  ...props
}: MagicIdProfileEmailOtpDialogProps) {
  const [otpValue, setOtpValue] = useState('');
  const {
    dataUpdatedAt,
    refetch: sendVerificationCode,
    isFetching: isSendVerificationCodePending,
  } = useSendVerificationCodeQuery(validEmail, { enabled: !!validEmail });
  const {
    mutateAsync: verifyEmailCode,
    isPending: isVerifyingCode,
    error: verifyEmailError,
    isError: isVerifyEmailError,
    reset: verifyEmailReset,
  } = useConfirmVerificationCodeMutation();
  const { setValue: setSelfDataChecksLocalStorage } = useSelfDataCheckLocalStorage();
  const [countdownTime, setCountdownTime] = useState(0);

  // reset mutation to hide its error from ui
  useEffect(() => {
    verifyEmailReset();
  }, [validEmail, verifyEmailReset]);

  useLayoutEffect(() => {
    if (!dataUpdatedAt) {
      return;
    }

    setCountdownTime(dataUpdatedAt + SEND_TIMEOUT_IN_MS);
  }, [dataUpdatedAt]);

  const handleVerifyButtonClick = async () => {
    try {
      await verifyEmailCode({ email: validEmail, code: otpValue });

      setSelfDataChecksLocalStorage((oldData) => {
        return {
          ...oldData,
          hasEmail: true,
        };
      });

      const text = `${validEmail} email successfully confirmed and added to MagicID`;

      toast.success(text);

      props.onOpenChange?.(false);
    } catch (error) {
      // NOTE: we are showing error in ui
    }
  };

  const handleResendCodeButtonClick = async () => {
    const { error, isError } = await sendVerificationCode();

    if (isError) {
      toast.error(getErrorMessage(error));
    }
  };

  const showResentCountdown = !isSendVerificationCodePending && countdownTime !== 0;
  const showResendButton = !showResentCountdown;
  const showError = isVerifyEmailError;
  const errorMessage = getErrorMessage(verifyEmailError);

  const isVerifyButtonDisabled = otpValue.length < OTP_LENGTH;

  return (
    <Dialog.Root {...props}>
      <Dialog.Portal>
        <Dialog.Overlay className="msq-dialog msq-dialog-animate msq-dialog-default">
          <Dialog.Close className="msq-dialog-close-button">
            <CrossLg className="msq-dialog-close-button-icon" />
          </Dialog.Close>
          <Dialog.Content className="msq-dialog-content msq-dialog-content-md">
            <div className="msq-dialog-body text-center pt-8 pb-4 md-msq:pb-8">
              <div className="flex flex-col items-center gap-4">
                <Dialog.Title className="msq-dialog-title">Email verification</Dialog.Title>
                <Dialog.Description className="text-body-md-a">
                  Enter the 6-digit code sent to your email
                  <br />
                  <span className="font-semibold">{validEmail}</span>
                </Dialog.Description>

                <div className="flex justify-center gap-2 px-2 w-full pt-2">
                  <OtpInput
                    data-qa="otp-email-input"
                    isError={isVerifyEmailError}
                    onChange={setOtpValue}
                    value={otpValue}
                    maxLength={OTP_LENGTH}
                  />
                </div>

                {showError && (
                  <p className="text-body-xs-a text-base-text-red mb-1">{errorMessage}</p>
                )}

                {showResentCountdown && (
                  <p className="text-body-sm-a text-base-text-tertiary">
                    Resend code in{' '}
                    <Countdown
                      date={countdownTime}
                      onComplete={() => setCountdownTime(0)}
                      renderer={({ formatted }) => (
                        // width of the content "00:00" to prevent layout shifts when time changes
                        <span className="min-w-[2.625rem] inline-block text-left">
                          {formatted.minutes}:{formatted.seconds}
                        </span>
                      )}
                    />
                  </p>
                )}

                {showResendButton && (
                  <button
                    onClick={handleResendCodeButtonClick}
                    disabled={isSendVerificationCodePending}
                    className="m-auto text-body-sm-a text-base-text-pink"
                  >
                    {isSendVerificationCodePending && (
                      <Loading className="animate-loading-spin size-6" />
                    )}
                    {!isSendVerificationCodePending && 'Resend code'}
                  </button>
                )}
              </div>

              <div className="msq-dialog-buttons mt-6">
                <Dialog.Close className="msq-btn msq-btn-lg msq-btn-secondary">Cancel</Dialog.Close>
                <button
                  disabled={isVerifyButtonDisabled}
                  onClick={handleVerifyButtonClick}
                  data-qa="otp-email-verify-button"
                  className={classNames('msq-btn msq-btn-lg msq-btn-primary', {
                    'msq-btn-loading': isVerifyingCode,
                  })}
                >
                  Verify
                </button>
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
